/**
 * 统一日志工具
 * 薄DDD方案 - 提供统一的日志记录，替代console.log/error
 */

export enum LogLevel {
  DEBUG = "debug",
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
  CRITICAL = "critical",
}

export interface LogEntry {
  timestamp: number;
  level: LogLevel;
  message: string;
  module?: string;
  context?: unknown;
  stack?: string;
}

/**
 * 日志配置
 */
interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  enableRemote: boolean;
  remoteUrl?: string;
  maxEntries: number;
}

/**
 * 统一日志类
 */
export class Logger {
  private static instance: Logger;
  private config: LoggerConfig;
  private logs: LogEntry[] = [];
  private enableProduction = process.env.NODE_ENV === "production";

  private constructor(config?: Partial<LoggerConfig>) {
    this.config = {
      level: this.enableProduction ? LogLevel.INFO : LogLevel.DEBUG,
      enableConsole: !this.enableProduction,
      enableRemote: false,
      maxEntries: 1000,
      ...config,
    };
  }

  /**
   * 获取单例实例
   */
  static getInstance(config?: Partial<LoggerConfig>): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(config);
    } else if (config) {
      Logger.instance.setConfig(config);
    }
    return Logger.instance;
  }

  /**
   * 调试日志
   */
  debug(message: string, context?: unknown, module?: string): void {
    this.log(LogLevel.DEBUG, message, context, module);
  }

  /**
   * 信息日志
   */
  info(message: string, context?: unknown, module?: string): void {
    this.log(LogLevel.INFO, message, context, module);
  }

  /**
   * 警告日志
   */
  warn(message: string, context?: unknown, module?: string): void {
    this.log(LogLevel.WARN, message, context, module);
  }

  /**
   * 错误日志
   */
  error(message: string, context?: unknown, module?: string): void {
    this.log(LogLevel.ERROR, message, context, module);
  }

  /**
   * 关键错误日志
   */
  critical(message: string, context?: unknown, module?: string): void {
    this.log(LogLevel.CRITICAL, message, context, module);
  }

  /**
   * 核心日志方法
   */
  private log(
    level: LogLevel,
    message: string,
    context?: unknown,
    module?: string,
  ): void {
    // 检查日志级别
    if (!this.shouldLog(level)) {
      return;
    }

    const entry: LogEntry = {
      timestamp: Date.now(),
      level,
      message,
      module,
      context,
    };

    // 获取堆栈信息（仅限错误和关键错误）
    if (level === LogLevel.ERROR || level === LogLevel.CRITICAL) {
      entry.stack =
        context instanceof Error ? context.stack : this.getStackTrace();
    }

    // 添加到日志记录
    this.logs.push(entry);

    // 限制日志数量
    if (this.logs.length > this.config.maxEntries) {
      this.logs.shift();
    }

    // 控制台输出
    if (this.config.enableConsole) {
      this.logToConsole(entry);
    }

    // 远程发送
    if (this.config.enableRemote && this.config.remoteUrl) {
      this.sendToRemote(entry);
    }
  }

  /**
   * 检查是否应该记录
   */
  private shouldLog(level: LogLevel): boolean {
    const levels = [
      LogLevel.DEBUG,
      LogLevel.INFO,
      LogLevel.WARN,
      LogLevel.ERROR,
      LogLevel.CRITICAL,
    ];
    const currentLevelIndex = levels.indexOf(this.config.level);
    const requestLevelIndex = levels.indexOf(level);

    return requestLevelIndex >= currentLevelIndex;
  }

  /**
   * 控制台输出
   */
  private logToConsole(entry: LogEntry): void {
    const timestamp = new Date(entry.timestamp).toISOString();
    const module = entry.module ? `[${entry.module}] ` : "";
    const message = `${timestamp} ${module}${entry.message}`;

    switch (entry.level) {
      case LogLevel.DEBUG:
        console.debug(message, entry.context || "");
        break;
      case LogLevel.INFO:
        console.info(message, entry.context || "");
        break;
      case LogLevel.WARN:
        console.warn(message, entry.context || "");
        break;
      case LogLevel.ERROR:
      case LogLevel.CRITICAL:
        console.error(message, entry.context || "", entry.stack || "");
        break;
    }
  }

  /**
   * 发送到远程
   */
  private sendToRemote(entry: LogEntry): void {
    if (!this.config.remoteUrl) return;

    // 使用 sendBeacon 进行异步发送
    const data = JSON.stringify(entry);

    if ("sendBeacon" in navigator) {
      navigator.sendBeacon(this.config.remoteUrl, data);
    } else {
      fetch(this.config.remoteUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: data,
      }).catch((error) => {
        console.warn("Failed to send log to remote:", error);
      });
    }
  }

  /**
   * 获取堆栈信息
   */
  private getStackTrace(): string {
    const error = new Error();
    return error.stack || "";
  }

  /**
   * 获取日志记录
   */
  getLogs(filter?: {
    level?: LogLevel;
    module?: string;
    since?: number;
  }): LogEntry[] {
    let filteredLogs = this.logs;

    if (filter) {
      if (filter.level) {
        filteredLogs = filteredLogs.filter((log) => log.level === filter.level);
      }

      if (filter.module) {
        filteredLogs = filteredLogs.filter(
          (log) => log.module === filter.module,
        );
      }

      if (filter.since != null) {
        const since = filter.since;
        filteredLogs = filteredLogs.filter((log) => log.timestamp >= since);
      }
    }

    return filteredLogs;
  }

  /**
   * 清空日志
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * 获取日志统计
   */
  getStats(): {
    total: number;
    byLevel: Record<LogLevel, number>;
    byModule: Record<string, number>;
  } {
    const stats = {
      total: this.logs.length,
      byLevel: {} as Record<LogLevel, number>,
      byModule: {} as Record<string, number>,
    };

    // 初始化计数器
    Object.values(LogLevel).forEach((level) => {
      stats.byLevel[level] = 0;
    });

    this.logs.forEach((log) => {
      stats.byLevel[log.level]++;

      if (log.module) {
        stats.byModule[log.module] = (stats.byModule[log.module] || 0) + 1;
      }
    });

    return stats;
  }

  /**
   * 导出日志
   */
  exportLogs(): string {
    return JSON.stringify(
      {
        timestamp: Date.now(),
        stats: this.getStats(),
        logs: this.logs,
      },
      null,
      2,
    );
  }

  /**
   * 设置配置
   */
  setConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
    this.trimLogs();
  }

  private trimLogs(): void {
    while (this.logs.length > this.config.maxEntries) {
      this.logs.shift();
    }
  }
}

/**
 * 模块化Logger工厂
 */
export function createModuleLogger(moduleName: string) {
  const logger = Logger.getInstance();

  return {
    debug: (message: string, context?: unknown) =>
      logger.debug(message, context, moduleName),
    info: (message: string, context?: unknown) =>
      logger.info(message, context, moduleName),
    warn: (message: string, context?: unknown) =>
      logger.warn(message, context, moduleName),
    error: (message: string, context?: unknown) =>
      logger.error(message, context, moduleName),
    critical: (message: string, context?: unknown) =>
      logger.critical(message, context, moduleName),
  };
}

/**
 * 全局Logger实例
 */
export const logger = Logger.getInstance();
